import { useState, useEffect } from 'react';
import { Loader2, Swords } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FORM_CLS = {
  W: 'bg-emerald-500 text-white',
  D: 'bg-zinc-500 text-white',
  L: 'bg-red-500 text-white',
  '?': 'bg-white/10 text-zinc-400',
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return ''; }
};

const FormPills = ({ items, teamName, logo }) => (
  <div className="flex-1 min-w-0">
    <div className="flex items-center gap-2 mb-2">
      {logo && <img src={logo} alt="" className="w-5 h-5 object-contain" />}
      <span className="text-[12px] font-bold text-white truncate">{teamName}</span>
    </div>
    <div className="flex gap-1.5">
      {items.length === 0 ? (
        <span className="text-[11px] text-zinc-500">No recent form</span>
      ) : items.map((f, i) => (
        <span key={i} className={`w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-black ${FORM_CLS[f.result] || FORM_CLS['?']}`}
          title={`${f.home_team} ${f.home_goals}-${f.away_goals} ${f.away_team}`} data-testid={`h2h-form-${i}`}>
          {f.result}
        </span>
      ))}
    </div>
  </div>
);

const MatchH2H = ({ fixtureId, homeTeam, awayTeam, homeLogo, awayLogo }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API_URL}/api/live-scores/match/${fixtureId}/h2h`)
      .then((r) => r.json())
      .then((d) => { if (alive) setData(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fixtureId]);

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-zinc-500" data-testid="h2h-loading"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading head-to-head…</div>;
  }

  const meetings = data?.meetings || [];
  const s = data?.summary || { total: 0, home_wins: 0, draws: 0, away_wins: 0 };
  const total = s.total || 0;
  const seg = (n) => (total ? `${(n / total) * 100}%` : '0%');

  return (
    <div className="px-3 py-3 space-y-4" data-testid="match-h2h">
      {/* Summary */}
      <div className="rounded-2xl bg-[var(--sk-surface)] p-4">
        <div className="flex items-center gap-2 mb-3 text-[12px] font-bold text-zinc-300">
          <Swords className="w-4 h-4 text-accent" /> Last {total || 0} meeting{total === 1 ? '' : 's'}
        </div>
        {total === 0 ? (
          <div className="text-[12px] text-zinc-500 text-center py-2">No previous meetings on record.</div>
        ) : (
          <>
            <div className="flex justify-between text-[11px] font-bold mb-1.5">
              <span className="text-emerald-400">{homeTeam} · {s.home_wins}W</span>
              <span className="text-zinc-400">{s.draws} Draw{s.draws === 1 ? '' : 's'}</span>
              <span className="text-sky-400">{s.away_wins}W · {awayTeam}</span>
            </div>
            <div className="flex h-2.5 rounded-full overflow-hidden bg-black/40">
              <div className="bg-emerald-500" style={{ width: seg(s.home_wins) }} />
              <div className="bg-zinc-500" style={{ width: seg(s.draws) }} />
              <div className="bg-sky-500" style={{ width: seg(s.away_wins) }} />
            </div>
          </>
        )}
      </div>

      {/* Recent form */}
      <div className="rounded-2xl bg-[var(--sk-surface)] p-4">
        <div className="text-[12px] font-bold text-zinc-300 mb-3">Recent form</div>
        <div className="flex gap-4">
          <FormPills items={data?.home_form || []} teamName={homeTeam} logo={homeLogo} />
          <FormPills items={data?.away_form || []} teamName={awayTeam} logo={awayLogo} />
        </div>
      </div>

      {/* Meetings list */}
      {meetings.length > 0 && (
        <div className="space-y-2">
          {meetings.map((m, i) => (
            <div key={i} className="rounded-2xl bg-[var(--sk-surface)] p-3.5" data-testid={`h2h-meeting-${i}`}>
              <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-2">
                <span>{fmtDate(m.date)}</span>
                <span className="truncate ml-2">{m.competition}</span>
              </div>
              <div className="flex items-center gap-2 text-[13px]">
                <div className="flex items-center gap-1.5 flex-1 min-w-0 justify-end">
                  <span className="truncate text-white font-medium">{m.home_team}</span>
                  {m.home_logo && <img src={m.home_logo} alt="" className="w-5 h-5 object-contain shrink-0" />}
                </div>
                <span className="px-2.5 py-1 rounded-lg bg-black/40 font-black text-white tabular-nums shrink-0">{m.home_goals} - {m.away_goals}</span>
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  {m.away_logo && <img src={m.away_logo} alt="" className="w-5 h-5 object-contain shrink-0" />}
                  <span className="truncate text-white font-medium">{m.away_team}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchH2H;
